import { docCirrusRpcClient } from "./rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "./diagnostic-logger";

/**
 * Whether the server could accept an eEB consent when last asked.
 *
 * Starts `false` and only ever becomes `true` on a successful answer, so a
 * kiosk that has not asked yet — or could not reach the bridge — does not walk
 * a patient into consent screens that must then fail.
 */
let lastKnownAvailability = false;

/**
 * Ask the bridge whether eEB may be offered, and remember the answer.
 *
 * Called when the hub loads, which is before any patient can reach the
 * check-in flow. Failures are swallowed into `false` — the answer is an
 * offer-or-not decision, not something to surface to a patient.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * await refreshEebAvailability();
 * ```
 */
export async function refreshEebAvailability(): Promise<boolean> {
  try {
    lastKnownAvailability = await docCirrusRpcClient.frontdesk.isEebAvailable();
  } catch (error) {
    frontdeskDiagnosticLogger.warn(
      "[eEB] availability unknown; not offering the eEB path",
      { error },
    );
    lastKnownAvailability = false;
  }
  return lastKnownAvailability;
}

/**
 * The last known answer, without asking again.
 *
 * The check-in machine needs this synchronously at creation; `false` until the
 * hub's refresh has answered is the fail-closed direction.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * const mayOfferEeb = FEATURE_FLAGS.eeb && isEebAvailableCached();
 * ```
 */
export function isEebAvailableCached(): boolean {
  return lastKnownAvailability;
}

/**
 * Reset the cache. Tests only.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * resetEebAvailabilityForTest();
 * ```
 */
export function resetEebAvailabilityForTest(): void {
  lastKnownAvailability = false;
}
